import { useMemo, useState } from 'react';
import type { LobbyInfo, SeriesCreateRequest } from '../api/types';
import { seriesFormatInfo } from '../domain/series';
import LobbyCard from '../features/lobbies/LobbyCard';
import LobbyChatDialog from '../features/lobbies/LobbyChatDialog';
import LobbyFormDialog, {
  type LobbyFormSubmit,
} from '../features/lobbies/LobbyFormDialog';
import LobbyInviteDialog from '../features/lobbies/LobbyInviteDialog';
import SeriesFormDialog from '../features/series/SeriesFormDialog';
import SeriesPanel from '../features/series/SeriesPanel';
import TournamentPanel from '../features/series/TournamentPanel';
import type { SeriesFormValues } from '../features/series/seriesForm';
import { trackedSeriesFor } from '../features/series/tracker';
import { useTrackedSeries } from '../features/series/useTrackedSeries';
import {
  useChangeLobbySettings,
  useCreateLobby,
  useCreateSeries,
  useDestroyLobby,
  useLaunchGame,
  useLobbies,
  usePool,
} from '../queries';
import { useConfig } from '../store/config';
import {
  Badge,
  Button,
  Card,
  Dialog,
  LoadingBlock,
  StateBlock,
  TextInput,
} from '../ui';
import { useToast } from '../ui/toast';

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div className="rounded-lg border border-line bg-surface-1/70 px-4 py-3">
      <p className="text-xs tracking-wide text-ink-faint uppercase">{label}</p>
      <p
        className={
          tone === 'warn'
            ? 'mt-1 text-2xl font-semibold text-warn'
            : tone === 'ok'
              ? 'mt-1 text-2xl font-semibold text-ok'
              : 'mt-1 text-2xl font-semibold text-ink'
        }
      >
        {value}
      </p>
    </div>
  );
}

export default function LobbiesPage() {
  const toast = useToast();
  const { config } = useConfig();
  const { data, isPending, isError, error, refetch, isFetching } = useLobbies();
  const { data: pool } = usePool();
  const activeLobbies = data?.active_lobbies;
  const { tracked, remember } = useTrackedSeries(activeLobbies);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formLobby, setFormLobby] = useState<LobbyInfo | null>(null);
  const [seriesOpen, setSeriesOpen] = useState(false);
  const [inviteLobby, setInviteLobby] = useState<LobbyInfo | null>(null);
  const [chatLobby, setChatLobby] = useState<LobbyInfo | null>(null);
  const [confirmDestroy, setConfirmDestroy] = useState<LobbyInfo | null>(null);
  const [search, setSearch] = useState('');
  const [workspace, setWorkspace] = useState<'live' | 'fixtures' | 'lobbies'>(
    'lobbies',
  );
  const [focusedSeries, setFocusedSeries] = useState<string | null>(null);
  const manageSeries = (id: string) => {
    setFocusedSeries(id);
    setWorkspace('live');
  };

  const createLobby = useCreateLobby();
  const createSeries = useCreateSeries();
  const changeSettings = useChangeLobbySettings();
  const launchGame = useLaunchGame();
  const destroyLobby = useDestroyLobby();

  const lobbies = useMemo(() => {
    const all = activeLobbies ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (lobby) =>
        lobby.game_name?.toLowerCase().includes(needle) ||
        String(lobby.lobby_id).includes(needle) ||
        lobby.bot_id?.includes(needle),
    );
  }, [activeLobbies, search]);

  const openCreate = () => {
    setFormMode('create');
    setFormLobby(null);
    setFormOpen(true);
  };

  const openEdit = (lobby: LobbyInfo) => {
    setFormMode('edit');
    setFormLobby(lobby);
    setFormOpen(true);
  };

  const handleSubmit = (submission: LobbyFormSubmit) => {
    if (submission.mode === 'create') {
      const { followUp } = submission;
      createLobby.mutate(submission.payload, {
        onSuccess: (result) => {
          if (submission.afterCreated) submission.afterCreated();
          else setFormOpen(false);
          toast.success('Lobby created', `ID ${result.lobby_id}`);
          if (!followUp) return;
          changeSettings.mutate(
            { botId: result.bot_id, settings: followUp },
            {
              onError: (mutationError) =>
                toast.error(
                  'Lobby created, but the extra settings did not apply',
                  `${(mutationError as Error).message}. Open Settings on the card to retry.`,
                ),
            },
          );
        },
        onError: (mutationError) =>
          toast.error('Create failed', (mutationError as Error).message),
      });
      return;
    }

    changeSettings.mutate(
      { botId: submission.botId, settings: submission.payload },
      {
        onSuccess: () => {
          setFormOpen(false);
          toast.success('Settings applied');
        },
        onError: (mutationError) =>
          toast.error('Update failed', (mutationError as Error).message),
      },
    );
  };

  const teamName = (teamId: string) =>
    config.teams.find((team) => team.id === teamId)?.name ?? '';

  const handleSeriesSubmit = (
    values: SeriesFormValues,
    payload: SeriesCreateRequest,
    afterCreated?: () => void,
  ) => {
    createSeries.mutate(payload, {
      onSuccess: (result) => {
        if (afterCreated) afterCreated();
        else setSeriesOpen(false);
        const format = seriesFormatInfo(payload.format);
        const now = Date.now();
        remember({
          botId: result.bot_id,
          firstLobbyId: String(result.lobby_id_str ?? result.lobby_id ?? ''),
          name: payload.game_name,
          format: payload.format,
          maxGames: result.max_games || format.maxGames,
          radiantTeam: teamName(values.radiantTeamId),
          direTeam: teamName(values.direTeamId),
          playersPerTeam: payload.players_per_team,
          startedAt: now,
          lastSeenAt: now,
        });
        toast.success(
          'Series created',
          `${format.label} on bot ${result.bot_id}. Both teams are being invited to game ${payload.game_number ?? 1}.`,
        );
      },
      onError: (mutationError) =>
        toast.error('Series failed', (mutationError as Error).message),
    });
  };

  const launch = (lobby: LobbyInfo) =>
    launchGame.mutate(
      { bot_id: lobby.bot_id },
      {
        onSuccess: () => toast.success('Game launching', lobby.game_name),
        onError: (mutationError) =>
          toast.error('Launch failed', (mutationError as Error).message),
      },
    );

  const destroyIsSeries =
    confirmDestroy !== null && confirmDestroy.controller_type === 'series';

  const destroy = (lobby: LobbyInfo) =>
    destroyLobby.mutate(lobby.bot_id, {
      onSuccess: () => {
        setConfirmDestroy(null);
        toast.success(
          destroyIsSeries ? 'Series cancelled' : 'Lobby destroyed',
          lobby.game_name,
        );
      },
      onError: (mutationError) =>
        toast.error(
          destroyIsSeries ? 'Cancel failed' : 'Destroy failed',
          (mutationError as Error).message,
        ),
    });

  const busyBots = pool?.status_counts?.busy ?? 0;
  const idleBots =
    (pool?.status_counts?.idle ?? 0) + (pool?.status_counts?.connected ?? 0);
  const anomalies = pool?.anomalies?.length ?? 0;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gold-300">
            Tournament operations
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">
            Match desk
          </h1>
          <p className="mt-2 text-sm text-ink-dim">
            Prepare the round. Check the lineups. Keep play moving.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openCreate}>Single lobby</Button>
          <Button variant="primary" onClick={() => setSeriesOpen(true)}>
            Create series
          </Button>
        </div>
      </header>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Active lobbies" value={data?.total_lobbies ?? 0} />
        <StatTile label="Bots in a lobby" value={busyBots} />
        <StatTile label="Bots available" value={idleBots} tone="ok" />
        <StatTile
          label="Pool anomalies"
          value={anomalies}
          tone={anomalies > 0 ? 'warn' : undefined}
        />
      </div>

      <nav
        aria-label="Match desk views"
        className="flex gap-1 border-b border-line"
      >
        {(
          [
            ['lobbies', 'Lobby controls'],
            ['live', 'Live series'],
            ['fixtures', 'Fixtures & queue'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-current={workspace === id ? 'page' : undefined}
            onClick={() => setWorkspace(id)}
            className={`border-b-2 px-4 py-3 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-gold-300 ${workspace === id ? 'border-gold-300 text-gold-300' : 'border-transparent text-ink-dim hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <div hidden={workspace !== 'fixtures'}>
        <TournamentPanel onManageSeries={manageSeries} />
      </div>
      <div hidden={workspace !== 'live'}>
        <SeriesPanel
          focusedSeries={focusedSeries}
          onClearFocus={() => setFocusedSeries(null)}
        />
      </div>

      <div hidden={workspace !== 'lobbies'}>
        <Card
          title="Lobbies"
          description={isFetching ? 'Refreshing…' : 'Updates every few seconds'}
          actions={
            <>
              <TextInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter by name, lobby or bot"
                className="h-8 w-52 text-xs"
                aria-label="Filter lobbies"
              />
              <Button size="sm" onClick={() => void refetch()}>
                Refresh
              </Button>
              <Button size="sm" onClick={() => setSeriesOpen(true)}>
                Create series
              </Button>
              <Button size="sm" variant="primary" onClick={openCreate}>
                Create lobby
              </Button>
            </>
          }
        >
          {isPending ? (
            <LoadingBlock label="Loading lobbies…" />
          ) : isError ? (
            <StateBlock
              tone="bad"
              title="Could not load lobbies"
              detail={(error as Error).message}
              action={
                <Button size="sm" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
            />
          ) : lobbies.length === 0 ? (
            <StateBlock
              title={
                search ? 'No lobbies match that filter' : 'No active lobbies'
              }
              detail={
                search
                  ? undefined
                  : 'Create a single lobby, or a best-of series that a bot runs from first invite to last game.'
              }
              action={
                !search ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button size="sm" onClick={() => setSeriesOpen(true)}>
                      Create series
                    </Button>
                    <Button size="sm" variant="primary" onClick={openCreate}>
                      Create lobby
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-4 xl:grid-cols-2">
              {lobbies.map((lobby) => (
                <LobbyCard
                  key={`${lobby.bot_id}-${lobby.lobby_id}`}
                  lobby={lobby}
                  tracked={trackedSeriesFor(lobby, tracked)}
                  busyAction={
                    launchGame.isPending &&
                    launchGame.variables?.bot_id === lobby.bot_id
                      ? 'launch'
                      : destroyLobby.isPending &&
                          destroyLobby.variables === lobby.bot_id
                        ? 'destroy'
                        : undefined
                  }
                  actions={{
                    onInvite: setInviteLobby,
                    onChat: setChatLobby,
                    onEdit: openEdit,
                    onDuplicate: (lobby) => {
                      setFormMode('create');
                      setFormLobby(lobby);
                      setFormOpen(true);
                    },
                    onLaunch: launch,
                    onDestroy: setConfirmDestroy,
                    onManageSeries: (lobby) =>
                      manageSeries(lobby.automation_series_id ?? ''),
                  }}
                />
              ))}
            </div>
          )}

          {data?.note && (
            <p className="mt-4 text-xs text-ink-faint">
              <Badge tone="neutral">note</Badge> {data.note}
            </p>
          )}
        </Card>
      </div>

      <LobbyFormDialog
        open={formOpen}
        mode={formMode}
        lobby={formLobby}
        submitting={createLobby.isPending || changeSettings.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <SeriesFormDialog
        open={seriesOpen}
        submitting={createSeries.isPending}
        onClose={() => setSeriesOpen(false)}
        onSubmit={handleSeriesSubmit}
      />

      <LobbyInviteDialog
        lobby={inviteLobby}
        onClose={() => setInviteLobby(null)}
      />
      <LobbyChatDialog lobby={chatLobby} onClose={() => setChatLobby(null)} />

      <Dialog
        open={confirmDestroy !== null}
        onClose={() => setConfirmDestroy(null)}
        size="sm"
        title={destroyIsSeries ? 'Cancel this series?' : 'Destroy this lobby?'}
        description={
          destroyIsSeries
            ? 'The bot closes the lobby and gives up the whole series: the remaining games are not played. To pick it up later, create the series again starting at the right game with the score so far.'
            : 'The bot leaves and the lobby is gone. This cannot be undone.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDestroy(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={destroyLobby.isPending}
              onClick={() => confirmDestroy && destroy(confirmDestroy)}
            >
              {destroyIsSeries ? 'Cancel series' : 'Destroy lobby'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-dim">
          {confirmDestroy?.game_name} · bot {confirmDestroy?.bot_id}
        </p>
      </Dialog>
    </div>
  );
}
